
import React from 'react';
import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Heart, MessageSquare, Bell, User, BookOpen, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Notification } from '@/types/notifications';
import { Badge } from '@/components/ui/badge';

interface NotificationItemProps {
  notification: Notification;
  onRead: (id: string) => void;
}

export const NotificationItem = ({ notification, onRead }: NotificationItemProps) => {
  const getNotificationLink = () => {
    switch (notification.type) {
      case 'like':
        return notification.story_id ? `/story/${notification.story_id}` : '#';
      case 'comment':
        return notification.episode_id && notification.story_id 
          ? `/story/${notification.story_id}/episode/${notification.episode_id}` 
          : '#';
      case 'new_episode':
        return notification.episode_id && notification.story_id 
          ? `/story/${notification.story_id}/episode/${notification.episode_id}` 
          : '#';
      case 'follow':
        return notification.sender_id ? `/author/${notification.sender_id}` : '#';
      default:
        return '#';
    }
  };
  
  const getNotificationIcon = () => {
    switch (notification.type) {
      case 'like':
        return <Heart className="text-pink-500" size={16} />;
      case 'comment':
        return <MessageSquare className="text-blue-500" size={16} />;
      case 'follow':
        return <User className="text-green-500" size={16} />;
      case 'new_episode':
        return <BookOpen className="text-purple-500" size={16} />;
      case 'system':
        return <Bell className="text-gray-500" size={16} />;
      default:
        return <Bell className="text-gray-500" size={16} />;
    }
  };

  const getNotificationTypeBadge = () => {
    switch (notification.type) {
      case 'like':
        return <Badge variant="outline" className="bg-pink-50 text-pink-600 border-pink-200">Like</Badge>;
      case 'comment':
        return <Badge variant="outline" className="bg-blue-50 text-blue-600 border-blue-200">Comment</Badge>;
      case 'follow':
        return <Badge variant="outline" className="bg-green-50 text-green-600 border-green-200">Follow</Badge>;
      case 'new_episode':
        return <Badge variant="outline" className="bg-purple-50 text-purple-600 border-purple-200">New Episode</Badge>;
      case 'system':
        return <Badge variant="outline" className="bg-gray-50 text-gray-600 border-gray-200">System</Badge>;
      default:
        return <Badge variant="outline" className="bg-gray-50 text-gray-600 border-gray-200">{notification.type}</Badge>;
    }
  };
  
  const handleClick = () => {
    if (!notification.is_read) {
      onRead(notification.id);
    }
  };
  
  return (
    <div 
      className={cn(
        "relative group",
        !notification.is_read && "bg-blue-50"
      )}
    >
      {!notification.is_read && (
        <div className="absolute left-0 top-0 bottom-0 w-1 bg-blue-400"></div>
      )}
      <Link 
        to={getNotificationLink()} 
        className="block p-4 hover:bg-gray-50 transition-colors"
        onClick={handleClick}
      >
        <div className="flex items-start">
          <div className="mr-3 mt-0.5">
            <Avatar className="h-9 w-9">
              {notification.sender_avatar ? (
                <AvatarImage src={notification.sender_avatar} alt={notification.sender_name || 'User'} />
              ) : (
                <AvatarFallback className="bg-gray-100 text-gray-600">
                  {getNotificationIcon()}
                </AvatarFallback>
              )}
            </Avatar>
          </div>
          <div className="flex-1">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  {getNotificationTypeBadge()}
                  {!notification.is_read && (
                    <span className="inline-block h-2 w-2 rounded-full bg-blue-500"></span>
                  )}
                </div>
                <p className="text-sm">
                  {notification.sender_name && (
                    <span className="font-medium">{notification.sender_name}</span>
                  )}
                  {' '}
                  <span className="text-gray-600">{notification.content}</span>
                </p>
              </div>
              <span className="text-xs text-gray-500 whitespace-nowrap ml-2">
                {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
              </span>
            </div>
          </div>
        </div>
      </Link>
      {!notification.is_read && (
        <button
          onClick={() => onRead(notification.id)}
          className="absolute right-2 top-2 opacity-0 group-hover:opacity-100 transition-opacity bg-white p-1 rounded-full shadow-sm border"
          title="Mark as read"
        >
          <Check size={14} className="text-green-500" />
        </button>
      )}
    </div>
  );
};
